import React, { useState, useEffect, useCallback } from 'react';
import { FiUsers, FiPlus, FiX, FiStar, FiAlertCircle } from 'react-icons/fi';
import {
  getCrews, createCrew, updateCrew, addCrewMember, removeCrewMember, setCrewMemberRole,
} from '../services/api';

// Crew roster management, sat on the Team page.
//
// Crews drive the Command Center board: each one is a column, and its CHIEF is what
// decides which job that column shows. So changing a chief here changes what the wall
// display attributes to whom — worth doing through a real screen rather than the API.
//
// Removing someone end-dates their stint rather than deleting it, so "who was on Will's
// crew in August" stays answerable. Nothing here destroys history.

const PALETTE = ['#2563eb', '#16a34a', '#d97706', '#7c3aed', '#dc2626', '#0891b2'];

export default function CrewManager({ team = [] }) {
  const [crews, setCrews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(null);      // crew id showing its add-member picker
  const [newCrewName, setNewCrewName] = useState('');
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await getCrews();
      setCrews(res.data || []);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load crews');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    setBusy(true); setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'That did not save');
    }
    setBusy(false);
  };

  const activeTeam = team.filter((m) => m.is_active !== false);

  if (loading) return <div style={{ padding: 20, color: '#6b7280' }}>Loading crews…</div>;

  return (
    <div style={{
      backgroundColor: 'white', borderRadius: 12, padding: 20,
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 24,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <h2 style={{ margin: 0, fontSize: 17, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiUsers /> Field Crews
        </h2>
        <button
          onClick={() => setShowNew((v) => !v)}
          style={{
            marginLeft: 'auto', padding: '6px 12px', border: '1px solid #d1d5db',
            background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 13,
            display: 'inline-flex', alignItems: 'center', gap: 5,
          }}
        >
          <FiPlus size={13} /> New crew
        </button>
      </div>
      <p style={{ color: '#6b7280', fontSize: 13, margin: '0 0 14px' }}>
        Each crew is a column on the Command Center board, and the <strong>chief</strong> decides
        which job that column shows. Removing someone keeps their history — it records the date
        they came off, it doesn't erase them.
      </p>

      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
          borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <FiAlertCircle /> {error}
        </div>
      )}

      {showNew && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <input
            value={newCrewName}
            onChange={(e) => setNewCrewName(e.target.value)}
            placeholder="Crew name (usually the chief's first name)"
            style={{ flex: 1, padding: 9, border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}
          />
          <button
            disabled={busy || !newCrewName.trim()}
            onClick={() => run(async () => {
              await createCrew({ name: newCrewName.trim() });
              setNewCrewName(''); setShowNew(false);
            })}
            style={{
              padding: '9px 16px', border: 'none', borderRadius: 8, fontWeight: 600,
              background: newCrewName.trim() ? '#16a34a' : '#d1d5db', color: '#fff',
              cursor: newCrewName.trim() ? 'pointer' : 'not-allowed',
            }}
          >
            Create
          </button>
        </div>
      )}

      {crews.length === 0 && (
        <div style={{ color: '#6b7280', fontStyle: 'italic' }}>No crews yet.</div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
        {crews.map((crew) => {
          const onCrew = new Set(crew.members.map((m) => m.team_member_id));
          const available = activeTeam.filter((m) => !onCrew.has(m.id));
          return (
            <div key={crew.id} style={{
              flex: '1 1 300px', minWidth: 280, border: '1px solid #e5e7eb',
              borderRadius: 10, borderTop: `4px solid ${crew.color}`, padding: 14,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{crew.name}</div>
                <select
                  value={crew.color}
                  disabled={busy}
                  onChange={(e) => run(() => updateCrew(crew.id, { color: e.target.value }))}
                  title="Board colour"
                  style={{
                    marginLeft: 'auto', width: 34, height: 26, padding: 0, cursor: 'pointer',
                    border: '1px solid #d1d5db', borderRadius: 6, background: crew.color, color: 'transparent',
                  }}
                >
                  {PALETTE.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {!crew.chief_name && (
                <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>
                  No chief — the board can't tell which job is this crew's.
                </div>
              )}

              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {crew.members.length === 0 && (
                  <div style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>Nobody on this crew</div>
                )}
                {crew.members.map((m) => {
                  const isChief = m.role === 'Chief';
                  return (
                    <div key={m.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      background: isChief ? '#f0fdf4' : '#f9fafb',
                      border: `1px solid ${isChief ? '#bbf7d0' : '#f3f4f6'}`,
                      borderRadius: 8, padding: '7px 10px',
                    }}>
                      <div style={{ fontSize: 14, fontWeight: isChief ? 700 : 500 }}>{m.name}</div>
                      {isChief && (
                        <span style={{
                          fontSize: 10, fontWeight: 800, letterSpacing: '0.06em',
                          background: '#16a34a', color: '#fff', padding: '2px 6px', borderRadius: 4,
                        }}>CHIEF</span>
                      )}
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                        {!isChief && (
                          <button
                            disabled={busy}
                            title="Make crew chief"
                            onClick={() => run(() => setCrewMemberRole(crew.id, m.team_member_id, 'Chief'))}
                            style={{
                              border: '1px solid #d1d5db', background: '#fff', borderRadius: 6,
                              padding: '3px 8px', fontSize: 12, cursor: 'pointer',
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                            }}
                          >
                            <FiStar size={11} /> Make chief
                          </button>
                        )}
                        <button
                          disabled={busy}
                          title="Take off this crew (keeps their history)"
                          onClick={() => run(() => removeCrewMember(crew.id, m.team_member_id))}
                          style={{
                            border: '1px solid #fecaca', background: '#fff', color: '#dc2626',
                            borderRadius: 6, padding: '3px 7px', fontSize: 12, cursor: 'pointer',
                          }}
                        >
                          <FiX size={12} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {adding === crew.id ? (
                <select
                  autoFocus
                  disabled={busy}
                  defaultValue=""
                  onChange={(e) => {
                    const id = parseInt(e.target.value, 10);
                    if (id) run(async () => { await addCrewMember(crew.id, { team_member_id: id }); setAdding(null); });
                  }}
                  style={{ width: '100%', marginTop: 10, padding: 8, border: '1px solid #d1d5db', borderRadius: 8 }}
                >
                  <option value="">Choose someone…</option>
                  {available.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              ) : (
                <button
                  disabled={busy || available.length === 0}
                  onClick={() => setAdding(crew.id)}
                  style={{
                    width: '100%', marginTop: 10, padding: 8, border: '1px dashed #d1d5db',
                    background: '#fff', borderRadius: 8, fontSize: 13,
                    cursor: available.length ? 'pointer' : 'not-allowed', color: '#374151',
                  }}
                >
                  <FiPlus size={12} /> Add someone
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
